import React from 'react';
import Barcode from 'react-barcode';
import { string, object, number } from 'prop-types';
import moment from 'moment';
import { get, each, size, map, filter, head, forEach, findIndex, isEmpty, join, includes } from 'lodash';
import { FlexContainer } from 'base-components';
import { Row, Column, Image, HasPermissions, NotFound, OrderShippingEstimate } from '../../src/components/common';
import JumboLogo from '../../assets/images/jumbo_logo.png';
import { Table, Text, Title, OrderPdfWrapper, DataField } from '../../src/components/pdf-commons';
import { getLooseProductsQuantity, formatPrice, formatProductQuantity, priceInt } from '../../src/components/pdf-commons/utils';
import { dashZero, baggingEnums, cleanOrderId } from '../../src/utils/misc';
import { GetPickerByEmail, Query } from '../../src/components/graphql';
import { GET_TRANSACTIONAL_MAIL } from '../../src/queries';
import { urlSisa, urlBase } from './../../src/components/utils/changeUrlSisa';
import SisaLogo from './../../assets/images/sisa.png';

const DispatchPDF = ({ match }) => {
    const orderId = get(match, 'params.id', null);

    return (
        <HasPermissions
            to={['order-detail-page:view']}
            otherwise={<NotFound firstTextLine='Página no encontrada' style={{ minHeight: '90vh' }} />}
        >
            <OrderPdfWrapper orderId={orderId}>
                { ({ data }) => {
                    const baggingDetail = get(data, 'assignInfo.baggingInfo.detail', {});
                    const items = get(data, 'assignInfo.items', []);
                    const looseProducts = getLooseProductsQuantity(data);
                    const noStockAmount = get(data, 'assignInfo.noStockAmount', 0);
                    const removedItems = get(data, 'assignInfo.removedItems', []);
                    const totalBagging = size(baggingDetail) + looseProducts;
                    return (
                        <>
                            <DispatchMessage
                                orderId={orderId}
                                data={data}
                                baggingDetail={baggingDetail}
                                looseProducts={looseProducts}
                                totalBagging={totalBagging}
                            />
                            <BoxesAndBags baggingDetail={baggingDetail} />
                            <LooseProducts count={looseProducts} />
                            <TotalBagging count={totalBagging} />
                            <SentProducts items={items} />
                            <MissingProducts items={[...items, ...removedItems]} />
                            <Query query={GET_TRANSACTIONAL_MAIL} variables={{ id: orderId }}>
                                { ({ data }) => {
                                    const getTransactionalMail = get(data, 'getTransactionalMail', []);
                                    return <Totals mailData={getTransactionalMail} noStockAmount={noStockAmount} />;
                                } }
                            </Query>
                        </>
                    );
                } }
            </OrderPdfWrapper>
        </HasPermissions>
    );
};

const DispatchMessage = ({ orderId, baggingDetail, data, looseProducts, totalBagging }) => {
    const selectedSlaLabel = () => {
        switch (get(data, 'logisticsInfo.selectedSla', '')) {
        case 'Despacho Especial':
        case 'Despacho Express':
        case 'Despacho a Domicilio':
            return 'Dirección de despacho';
        default:
            return 'Dirección de retiro';
        }
    };
    let changeUrl = false;
    if (urlSisa() == urlBase()) {
        changeUrl = true;
    }
    return (
        <div style={{ pageBreakBefore: 'always' }}>
            <Row>
                <Column>
                    <Row align='baseline'>
                        <Text text='Gracias por comprar en&nbsp;' strong size={24} />
                        <Image src={changeUrl ? SisaLogo : JumboLogo} height={15} width={80} grayscale={100} contrast={1000} />
                    </Row>
                    <Row>
                        <Text text={`Nº ${cleanOrderId(orderId)}`} semibold size={18} />
                    </Row>
                </Column>
                <Column noFlex>
                    <Barcode
                        value={get(data, 'assignInfo.eanOrder', 'server code error')}
                        width={0.9}
                        height={40}
                        fontSize={14}
                        marginTop={10}
                    />
                </Column>
            </Row>
            <Row style={{ borderRadius: '3px', width: '100%', border: '1px solid #000000' }} mT={10} mB={20}>
                <Column style={{ display: 'block' }} mY={15} mL={15} mR={20}>
                    <DataField
                        label='Nombre del cliente'
                        value={`${get(data, 'customer.firstName', '')} ${get(data, 'customer.lastName', '')}`}
                    />
                    <DataField label='Teléfono' value={`${get(data, 'customer.phone', '')}`} />
                    <DataField label={selectedSlaLabel()} value={`${get(data, 'selectedAddress.addressLine', '')}`} />
                    <DataField label='Quien recibe' value={`${get(data, 'selectedAddress.receiverName', '')}`} />
                    <DataField label='Observaciones' value={`${get(data, 'selectedAddress.reference', '-') || '-'}`} />
                </Column>
                <Column style={{ display: 'block' }} mY={15} mR={15}>
                    <GetPickerByEmail email={get(data, 'assignInfo.idUser', 'null')}>
                        {({ fullName }) => <DataField label='Shopper' value={fullName} /> }
                    </GetPickerByEmail>
                    <DataField label='Embolsados' value={dashZero(baggingDetail && baggingDetail.length)} />
                    <DataField label='Productos sueltos' value={dashZero(looseProducts)} />
                    <DataField
                        label='Total bultos'
                        value={dashZero(totalBagging)}
                    />
                    <OrderShippingEstimate {...data}>
                        { ({ deliveryTimeHourBySla }) => (
                            <DataField
                                label='Fecha de compromiso'
                                value={`${moment(get(data, 'logisticsInfo.shippingETA', ''), 'YYYY-MM-DD HH:mm:ss').format('DD-MM-YYYY')}`}
                            />
                        ) }
                    </OrderShippingEstimate>
                    <DataField label='Hora de facturación' value={`${get(data, 'assignInfo.posDetail.0.hour', '-')}`} />
                </Column>
            </Row>
            <Row>
                <Text
                    text='Aquí encontrarás el detalle de los productos que van en cada bolsa, para facilitar la revisión de tu compra.'
                    size={11}
                />
            </Row>
        </div>
    );
};

const BoxesAndBags = ({ baggingDetail }) => {
    let sortedByType = [];
    forEach(baggingDetail, ({ type, randomId, id }) => {
        const index = findIndex(sortedByType, ['type', type]);
        if (index < 0) {
            sortedByType = [...sortedByType, { type, value: [`${randomId ? '-' : id}`] }];
        } else {
            sortedByType[index] = { type: get(sortedByType[index], 'type'), value: [...get(sortedByType[index], 'value'), `${randomId ? '-' : id}`] };
        }
    });
    const data = {
        headers: [
            { key: 'qty', value: 'Cant' },
            { key: 'type', value: 'Formato' },
            { key: 'code', value: 'Código' }
        ],
        body: map(sortedByType, ({ type, value }) => {
            return {
                qty: `${size(value)} un`,
                type: baggingEnums[type],
                code: join(value, ' - ')
            };
        })
    };
    return !isEmpty(get(data, 'body', [])) && <Table counter={size(baggingDetail)} title='Embolsados' headers={data.headers} body={data.body} />;
};

const LooseProducts = ({ count }) => {
    if (!count) return null;
    return (
        <Row style={{ lineHeight: '2.7' }}>
            <Column>
                <Title marginTop={0} text='Productos sueltos' />
            </Column>
            <Column noFlex>
                <Title marginTop={0} text={count} />
            </Column>
        </Row>
    );
};

const TotalBagging = ({ count }) => {
    if (!count) return null;
    return (
        <Row style={{ borderBottom: '2px solid #333333', borderTop: '2px solid #333333', lineHeight: '2.7', margin: '0px 0 30px 0' }}>
            <Column>
                <Title marginTop={0} text='Total bultos' />
            </Column>
            <Column noFlex>
                <Title marginTop={0} text={count} />
            </Column>
        </Row>
    );
};

const SentProducts = ({ items }) => {
    let body = [];
    each(items, ({ unit, originalQuantity, pickingQuantity, ean, substitution, name, status, unpackaged }) => {
        if (includes(['OUT_STOCK_ACCEPTED'], status)) return null;
        if (includes(['FREE_PICKING_ACCEPTED', 'SUBSTITUTE_CONFIRMED'], status)) {
            each(substitution, (substitute, index) => {
                body.push({
                    qty: index === 0 && formatProductQuantity(originalQuantity, unit),
                    refId: index === 0 && ean,
                    desc: index === 0 && name,
                    subQty: formatProductQuantity(get(substitute, 'pickingQuantity'), get(substitute, 'unit')),
                    subCod: get(substitute, 'ean'),
                    subDesc: get(substitute, 'name'),
                    loose: get(substitute, 'unpackaged') ? 'Si' : '-',
                    rowStyle: size(substitution) !== (index + 1) ? { borderBottom: '0' } : null
                });
            });
        } else {
            body.push({
                qty: formatProductQuantity(status === 'PARTIAL_ACCEPTED' ? pickingQuantity : originalQuantity, unit),
                refId: ean,
                desc: name,
                subQty: '-',
                subCod: '-',
                subDesc: '-',
                loose: unpackaged ? 'Si' : '-'
            });
        }
    });
    const data = {
        headers: [
            { key: 'qty', value: 'Cant' },
            { key: 'refId', value: 'Código Ref ID' },
            { key: 'desc', value: 'Producto solicitado' },
            { key: 'subQty', value: 'Cant' },
            { key: 'subCod', value: 'Código' },
            { key: 'subDesc', value: 'Producto enviado (sustituto)' },
            { key: 'loose', value: 'Suelto' }
        ],
        body
    };
    return <Table title='Productos enviados' headers={data.headers} body={data.body} />;
};

const MissingProducts = ({ items }) => {
    const filteredItems = filter(items, ({ status }) => includes(['PARTIAL_ACCEPTED', 'OUT_STOCK_ACCEPTED', 'UNPICK_BY_STORE'], status));
    const body = map(filteredItems, ({ originalQuantity, pickingQuantity, ean, unit, name }) => {
        const quantity = Number(originalQuantity) - Number(pickingQuantity);
        return {
            qty: formatProductQuantity(quantity, unit),
            refId: ean,
            desc: name,
            subQty: '-',
            subCod: '-',
            subDesc: '-'
        };
    });
    const data = {
        headers: [
            { key: 'qty', value: 'Cant' },
            { key: 'refId', value: 'Código Ref ID' },
            { key: 'desc', value: 'Descripción producto' },
            { key: 'subQty', value: 'Cant' },
            { key: 'subCod', value: 'Código' },
            { key: 'subDesc', value: 'Descripción' }
        ],
        body
    };
    return size(body) > 0 && <Table title='Productos faltantes' headers={data.headers} body={data.body} />;
};

const Totals = ({ mailData, noStockAmount }) => {
    const mailDataResolver = ({ key: inputKey, field }) => get(head(filter(mailData, item => inputKey === item.key)), field, 'unknow field');
    const substituteDiscounts = mailDataResolver({ key: 'substitute-discounts', field: 'amount' });
    // const outStock = mailDataResolver({ key: 'out-stock', field: 'amount' });

    const thead = [
        { value: null },
        {
            value: null,
            style: { width: '50px' }
        },
        {
            value: 'Monto',
            style: { width: '80px', textAlign: 'right' }
        },
        { value: 'Descripción' }
    ];

    const tbody = [
        [
            {
                value: 'Productos solicitados',
                style: { backgroundColor: '#f4f4f4' }
            },
            {
                value: mailDataResolver({ key: 'order-amount', field: 'items' }),
                style: { textAlign: 'center' }
            },
            {
                value: formatPrice(mailDataResolver({ key: 'order-amount', field: 'amount' })),
                style: { textAlign: 'right' }
            },
            { value: 'Monto del pedido' }
        ],
        [
            {
                value: 'Costo del servicio',
                style: { backgroundColor: '#f4f4f4' }
            },
            { value: null },
            {
                value: formatPrice(mailDataResolver({ key: 'pickup-dispatch', field: 'amount' })),
                style: { textAlign: 'right' }
            },
            { value: 'Recolección + Despacho' }
        ],
        [
            {
                value: 'Prod. solicitados + Servicio',
                style: { fontWeight: 'bold' }
            },
            { value: null },
            {
                value: formatPrice(mailDataResolver({ key: 'reserved-amount', field: 'amount' })),
                style: { textAlign: 'right' }
            },
            {
                value: 'Monto del pedido + costo de entrega',
                style: { fontWeight: 'bold' }
            }
        ],
        [
            {
                value: 'Productos con sustitutos',
                style: { backgroundColor: '#f4f4f4' }
            },
            {
                value: mailDataResolver({ key: 'substitute-discounts', field: 'items' }),
                style: { textAlign: 'center' }
            },
            {
                value: `${priceInt(substituteDiscounts) > 0 ? '-' : ''}${formatPrice(substituteDiscounts)}`,
                style: { textAlign: 'right' }
            },
            { value: 'Monto descontado por productos de reemplazo de menor precio' }
        ],
        [
            {
                value: 'Productos sin stock',
                style: { backgroundColor: '#f4f4f4' }
            },
            {
                value: mailDataResolver({ key: 'out-stock', field: 'items' }),
                style: { textAlign: 'center' }
            },
            {
                value: `${noStockAmount < 0 ? '-' : ''}${formatPrice(Math.abs(noStockAmount))}`,
                style: { textAlign: 'right' }
            },
            { value: 'Monto descontado por productos no enviados' }
        ],
        [
            {
                value: 'Total facturado',
                style: { fontWeight: 'bold' }
            },
            { value: null },
            {
                value: formatPrice(mailDataResolver({ key: 'total-order', field: 'amount' })),
                style: { fontWeight: 'bold', textAlign: 'right' }
            },
            {
                value: 'Monto final del pedido',
                style: { fontWeight: 'bold' }
            }
        ]
    ];

    return (
        <>
            <Row style={{ borderBottom: '2px solid #333333', lineHeight: '2.7', marginTop: '30px' }}>
                <Column>
                    <Title text='Detalle de facturación' />
                </Column>
            </Row>
            <FlexContainer direction='row'>
                <table style={{ width: '100%' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #dbdbdb' }}>
                            { map(thead, ({ value, style = {} }) => (
                                <td style={{ ...style, padding: '10px' }}>
                                    <Text text={value} size={9} />
                                </td>
                            )) }
                        </tr>
                    </thead>
                    <tbody>
                        { map(tbody, element => (
                            <tr style={{ borderBottom: '1px solid #dbdbdb' }}>
                                { map(element, ({ value, style = {} }) => (
                                    <td style={{ ...style, padding: '10px' }}>
                                        <Text text={value} style={{ ...style }} size={9} />
                                    </td>
                                )) }
                            </tr>
                        )) }
                    </tbody>
                </table>
            </FlexContainer>
        </>
    );
};

DispatchPDF.propTypes = {
    match: object
};

DispatchMessage.propTypes = {
    orderId: string,
    baggingDetail: object,
    data: object,
    looseProducts: number,
    totalBagging: number
};

LooseProducts.propTypes = {
    count: number
};

TotalBagging.propTypes = {
    count: number
};

SentProducts.propTypes = {
    items: object
};

MissingProducts.propTypes = {
    items: object
};

Totals.propTypes = {
    mailData: object,
    noStockAmount: number
};

export default DispatchPDF;
